import { useEffect, useRef, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import ApiDatabase from 'server';
import { ArchiveIcon, BanIcon, Check, ChevronLeft, ChevronRight, CrossIcon, Loading2, QuestionMarkIcon } from '../../../../../assets/icons';
import { dateToEpoch, dateWordShort } from '../../../../../helper/helper';
import UseContext from '../../../../../hooks/useContext';
import usefetchdata from '../../../../../hooks/usefetchdata';
import Badge from '../../../../badge/badge';
import Button from '../../../../button';
import CopyToClipboard from '../../../../copyToClipboard';
import DropdownMenu from '../../../../DropdownMenu/DropdownMenu';
import UseModal from '../../../../modal/useModal';
import SlideOverBody from '../../../component/body';
import SlideOverHeader from '../../../component/header';
import useSlideOver from '../../../useSlideOver';
import Tooltip from '../../../../tooltips/Tooltips';
import CommentTempWorker from './commentTempWorker';
import HistoryTempWorker from './historyTempWorker';
import PayslipTempWorker from './payslipTempWorker';
import PreviewTempWorker from './previewTempWorker';
import ReviewTempWorker from './reviewTempWorker';

// Raisons d'archive connues
const ARCHIVE_REASONS = ['admin', '45 days absence', 'unavailable'];
// ID du vivier "Exclus de l'agence"
const EXCLUSION_VIVIER_ID = '64e8cfd10b4f73f442432c9f';

const SlideOverTempWorker = ({ setShowCV: setParentShowCV }) => {
  const { toggle } = UseModal();
  const { toggleSlideOver } = useSlideOver();
  const {
    objID,
    setObjID,
    setAdminType,
    setAdminTempWorkerId,
    setAdminUserId,
    removeAllParameters,
    openModal,
    setOpenModal,
    target,
    setTarget,
    modalData,
    modalDataReturn,
    slideOverCandidatesList,
    setSlideOverCandidatesList,
    slideOverCurrentIndex,
    setSlideOverCurrentIndex,
    setInfoVivier,
    usersVivier,
    setUsersVivier,
    slideOverMissionActions,
    setSlideOverMissionActions,
    tempWorkerCommentCountByUserId,
  } = UseContext();
  const { userData } = usefetchdata();
  const token = localStorage.getItem('xsrfToken');
  const previewRef = useRef(null);
  const [tempWorkerInfos, setTempWorkerInfos] = useState({
    tempWorker: {},
    user: {},
    viviers: [],
  });
  const [activeTab, setActiveTab] = useState(1);
  const [payslipInitialYear, setPayslipInitialYear] = useState(null);
  const [nbReviews, setNbReviews] = useState(0);
  const [nbComments, setNbComments] = useState(0);
  useEffect(() => {
    if (!objID) return;
    const count = tempWorkerCommentCountByUserId?.[objID.toString()];
    if (typeof count === 'number') {
      setNbComments(count);
    }
  }, [objID, tempWorkerCommentCountByUserId]);
  const [userArchiveReason, setUserArchiveReason] = useState(false);
  const [showCV, setShowCV] = useState(false);
  const [cvDocs, setCvDocs] = useState([]);
  const [isWaitingCV, setIsWaitingCV] = useState(false);
  const [cvLoadError, setCvLoadError] = useState(null);
  const cvLoadedRef = useRef(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuTriggerRef = useRef(null);
  const menuDropdownRef = useRef(null);
  const cvCacheRef = useRef({});
  const cvRequestIdRef = useRef(null);
  const objIdRef = useRef(objID);
  objIdRef.current = objID;

  const currentList = slideOverCandidatesList || [];

  const navigateTo = (newIndex) => {
    const nextId = currentList[newIndex];
    if (!nextId) return;
    setSlideOverCurrentIndex(newIndex);
    setCvDocs([]);
    setIsWaitingCV(false);
    setCvLoadError(null);
    cvLoadedRef.current = false;
    setIsLoadingData(true);
    setObjID(nextId);
  };

  const goToPrevious = () => {
    if (slideOverCurrentIndex > 0) navigateTo(slideOverCurrentIndex - 1);
  };

  const goToNext = () => {
    if (slideOverCurrentIndex < currentList.length - 1) navigateTo(slideOverCurrentIndex + 1);
  };

  const openDocument = (type, uri) => {
    if (type === 'cv') {
      setShowCV(true);
      loadCV();
    } else {
      setAdminType(type);
      setAdminTempWorkerId('');
      setAdminUserId(tempWorkerInfos?.tempWorker?.id_user);
      toggle('ad_file_view');
    }
  };

  const parseCvUrls = (data) => {
    const urls = Array.isArray(data?.urls) ? data.urls : [];
    return urls.filter(Boolean).map((item) => {
      const href = item.url || item.uri || '';
      let originalFileName = '';
      try {
        const u = new URL(href);
        originalFileName = decodeURIComponent(
          (u.pathname.split('/').pop() || '').trim()
        );
      } catch (e) {
        const clean = href.split('?')[0].split('#')[0];
        originalFileName = decodeURIComponent(
          (clean.split('/').pop() || '').trim()
        );
      }
      return {
        uri: href,
        fileName: originalFileName || 'document',
        fileType: item.type || 'application/pdf',
      };
    });
  };

  const loadCV = () => {
    const targetId = objIdRef.current;
    if (!targetId) return;

    cvRequestIdRef.current = targetId;

    if (Object.prototype.hasOwnProperty.call(cvCacheRef.current, targetId)) {
      const cached = cvCacheRef.current[targetId];
      setCvDocs(cached);
      setIsWaitingCV(false);
      setCvLoadError(
        Array.isArray(cached) && cached.length === 0
          ? 'Aucun CV disponible (fichier introuvable).'
          : null
      );
      return;
    }

    setIsWaitingCV(true);
    setCvLoadError(null);
    ApiDatabase.postPresignFiles(
      { token, idUser: targetId, type: 'cv' },
      (data) => {
        if (cvRequestIdRef.current !== targetId) return;
        const docs = parseCvUrls(data);
        cvCacheRef.current[targetId] = docs;
        setIsWaitingCV(false);
        setCvDocs(docs);
        setCvLoadError(
          docs.length === 0
            ? 'Aucun CV disponible (fichier introuvable).'
            : null
        );
      },
      (err) => {
        if (cvRequestIdRef.current !== targetId) return;
        console.log(err);
        setCvDocs([]);
        setIsWaitingCV(false);
        setCvLoadError('Impossible de charger le CV.');
      }
    );
  };

  const prefetchNextCV = () => {
    if (currentList.length === 0) return;
    const nextIndex = slideOverCurrentIndex + 1;
    if (nextIndex >= currentList.length) return;
    const nextId = currentList[nextIndex];
    if (!nextId || cvCacheRef.current[nextId]) return;

    ApiDatabase.getTempWorkerByIdUser(
      { token, idUser: nextId },
      (data) => {
        if (!data?.cv?.updated) return;
        ApiDatabase.postPresignFiles(
          { token, idUser: nextId, type: 'cv' },
          (presignData) => {
            cvCacheRef.current[nextId] = parseCvUrls(presignData);
          },
          () => {}
        );
      },
      () => {}
    );
  };

  // Réinitialiser cvDocs quand objID change
  useEffect(() => {
    if (!objID) {
      setCvDocs([]);
      setCvLoadError(null);
      cvLoadedRef.current = false;
    }
  }, [objID]);

  useEffect(() => {
    if (!isLoadingData && objID && currentList.length > 1 && (!showCV || cvDocs.length > 0)) {
      prefetchNextCV();
    }
  }, [isLoadingData, objID, slideOverCurrentIndex, showCV, cvDocs.length]);

  useEffect(() => {
    if (openModal) {
      toggle(openModal);
      setOpenModal(false);
    }
  }, [openModal]);

  useEffect(() => {
    if (target) {
      if (target === 'review') {
        setActiveTab(2);
        setTarget('');
      } else if (target === 'comment') {
        setActiveTab(3);
        setTarget('');
      } else if (target === 'payslip') {
        setActiveTab(5);
        if (modalData?.payslipYear) {
          setPayslipInitialYear(Number(modalData.payslipYear));
        }
        setTarget('');
      }
    }
  }, [target]);

  useEffect(() => {
    if (
      activeTab === 1 &&
      target === 'vivier' &&
      tempWorkerInfos.viviers.length > 0
    ) {
      if (previewRef.current && previewRef.current.scrollToVivier) {
        previewRef.current.scrollToVivier();
        setTarget('');
      }
    }
  }, [activeTab, target, tempWorkerInfos]);

  useEffect(() => {
    if (objID) {
      const requestedId = objID;
      setIsLoadingData(true);
      ApiDatabase.getUserById(
        { token, id_user: objID },
        (data) => {
          if (objIdRef.current !== requestedId) return;
          const birthDay = dateToEpoch(data.birthday);
          setTempWorkerInfos((prevData) => ({
            ...prevData,
            user: {
              ...data,
              birthday:
                ('0' + birthDay.getDate()).toString().slice(-2) +
                '/' +
                ('0' + (parseInt(birthDay.getMonth()) + 1))
                  .toString()
                  .slice(-2) +
                '/' +
                birthDay.getFullYear(),
            },
          }));
          ApiDatabase.getTempWorkerByIdUser(
            { token, idUser: objID },
            (data) => {
              if (objIdRef.current !== requestedId) return;
              setTempWorkerInfos((prevData) => ({
                ...prevData,
                tempWorker: data,
              }));
              setIsLoadingData(false);
              if (data?.cv?.updated) {
                setShowCV(true);
                loadCV();
              } else {
                setShowCV(false);
              }
            },
            (err) => {
              if (objIdRef.current !== requestedId) return;
              if (err.name !== 'CanceledError') {
                console.log(err);
              }
              setIsLoadingData(false);
            }
          );
        },
        (err) => {
          if (objIdRef.current !== requestedId) return;
          if (err.name !== 'CanceledError') {
            console.log(err);
          }
          setIsLoadingData(false);
        }
      );

      ApiDatabase.getNbReviewsTempWorker(
        { token, idUser: objID },
        (data) => {
          if (objIdRef.current !== requestedId) return;
          setNbReviews(data.nbReviews);
        },
        (err) => {
          if (err.name !== 'CanceledError') {
            console.log(err);
          }
        }
      );

      ApiDatabase.getNbCommentsByTempWorker(
        { token, idUser: objID },
        (data) => {
          if (objIdRef.current !== requestedId) return;
          setNbComments(data.count);
        },
        (err) => {
          if (err.name !== 'CanceledError') {
            console.log(err);
          }
        }
      );

      ApiDatabase.getUserVivierArchiveReason(
        { token, idUser: objID },
        (data) => {
          if (objIdRef.current !== requestedId) return;
          setUserArchiveReason(data?.reason || false);
        },
        (err) => {
          if (err.name !== 'CanceledError') {
            console.log(err);
          }
        }
      );

      ApiDatabase.getUserViviers(
        { token, idUser: objID },
        (data) => {
          if (objIdRef.current !== requestedId) return;
          setTempWorkerInfos((prevData) => ({
            ...prevData,
            viviers: data || [],
          }));
        },
        (err) => {
          if (err.name !== 'CanceledError') {
            console.log(err);
          }
        }
      );
    }
  }, [objID]);

  useEffect(() => {
    if (!modalDataReturn) return;
    if (modalDataReturn?.action === 'payslips_deleted' && modalDataReturn.id_temp_worker_bestt) {
      const idBestt = tempWorkerInfos?.user?.id_bestt;
      if (idBestt && modalDataReturn.id_temp_worker_bestt.includes(idBestt)) {
        const y = String(modalDataReturn.year);
        const m = String(modalDataReturn.month).padStart(2, '0');
        setTempWorkerInfos((prev) => ({
          ...prev,
          tempWorker: {
            ...prev.tempWorker,
            payslip: (prev.tempWorker?.payslip || []).filter(
              (p) => !(p.year === y && p.month === m)
            ),
          },
        }));
      }
    } else if (modalDataReturn?.action === 'payslips_imported' && modalDataReturn.id_temp_worker_bestt) {
      const idBestt = tempWorkerInfos?.user?.id_bestt;
      if (idBestt && modalDataReturn.id_temp_worker_bestt.includes(idBestt)) {
        const y = String(modalDataReturn.year);
        const m = String(modalDataReturn.month).padStart(2, '0');
        setTempWorkerInfos((prev) => {
          const existing = (prev.tempWorker?.payslip || []).find(
            (p) => p.year === y && p.month === m
          );
          if (existing) return prev;
          return {
            ...prev,
            tempWorker: {
              ...prev.tempWorker,
              payslip: [
                ...(prev.tempWorker?.payslip || []),
                { year: y, month: m, status: 'imported' },
              ],
            },
          };
        });
      }
    }
  }, [modalDataReturn]);

  useEffect(() => {
    const socket = ApiDatabase.getSocket();
    if (!socket) return;

    const idBestt = tempWorkerInfos?.user?.id_bestt;
    if (!idBestt) return;

    const onImport = (data) => {
      if (!data?.updatedPayslips?.length) return;
      const relevant = data.updatedPayslips.filter(
        (u) => u.id_temp_worker_bestt === idBestt
      );
      if (relevant.length === 0) return;

      setTempWorkerInfos((prev) => {
        const payslips = [...(prev.tempWorker?.payslip || [])];
        for (const p of relevant) {
          const idx = payslips.findIndex(
            (ps) => ps.year === p.year && ps.month === p.month
          );
          const entry = { year: p.year, month: p.month, amount: p.amount };
          if (idx >= 0) {
            payslips[idx] = { ...payslips[idx], ...entry };
          } else {
            payslips.push(entry);
          }
        }
        return {
          ...prev,
          tempWorker: { ...prev.tempWorker, payslip: payslips },
        };
      });
    };

    socket.on('payslipImportCompleted', onImport);
    return () => socket.off('payslipImportCompleted', onImport);
  }, [tempWorkerInfos?.user?.id_bestt]);

  // Recharger les viviers après modification (exclusion/annulation)
  useEffect(() => {
    if (usersVivier && usersVivier.length > 0 && objID) {
      ApiDatabase.getUserViviers(
        { token, idUser: objID },
        (data) => {
          setTempWorkerInfos((prevData) => ({
            ...prevData,
            viviers: data || [],
          }));
        },
        (err) => {
          if (err.name !== 'CanceledError') {
            console.log(err);
          }
        }
      );
      setUsersVivier([]);
    }
  }, [usersVivier, objID]);

  const closeSlideOver = () => {
    toggleSlideOver();
    removeAllParameters();
    setShowCV(false);
    setCvDocs([]);
    setCvLoadError(null);
    setSlideOverMissionActions(null);
  };

  const removeFromPagination = (idToRemove) => {
    const newList = currentList.filter((id) => id !== idToRemove);
    if (newList.length === 0) {
      closeSlideOver();
      return;
    }
    setSlideOverCandidatesList(newList);
    const nextIndex = Math.min(slideOverCurrentIndex, newList.length - 1);
    setSlideOverCurrentIndex(nextIndex);
    setCvDocs([]);
    setCvLoadError(null);
    cvLoadedRef.current = false;
    setIsLoadingData(true);
    setObjID(newList[nextIndex]);
  };

  const handleMissionAction = (actionFn, immediate = false) => {
    if (!actionFn || !objID) return;
    actionFn(objID);
    if (immediate) {
      removeFromPagination(objID);
    }
  };

  useEffect(() => {
    if (!slideOverMissionActions) return;
    if (modalDataReturn === 'mission assigned') {
      closeSlideOver();
    } else if (modalDataReturn?.action === 'refuse_candidat' && modalDataReturn?.idTempWorker) {
      removeFromPagination(modalDataReturn.idTempWorker);
    }
  }, [modalDataReturn]);

  useEffect(() => {
    if (!slideOverMissionActions) return;
    const onKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.key === 'x' && slideOverMissionActions.onRefuse) {
        e.preventDefault();
        handleMissionAction(slideOverMissionActions.onRefuse, slideOverMissionActions.isRefused);
      } else if (e.key === 'g' && slideOverMissionActions.onPendingContact) {
        e.preventDefault();
        handleMissionAction(slideOverMissionActions.onPendingContact, true);
      } else if (e.key === 'Enter' && slideOverMissionActions.onAssign) {
        e.preventDefault();
        handleMissionAction(slideOverMissionActions.onAssign);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [slideOverMissionActions, objID, currentList, slideOverCurrentIndex]);

  const excludeFromAgency = () => {
    setInfoVivier({
      listUsers: [tempWorkerInfos?.user?._id],
    });
    toggle('exclude_agency');
    setIsMenuOpen(false);
  };

  // Calculer la position du menu dropdown
  useEffect(() => {
    if (isMenuOpen && menuTriggerRef.current) {
      const rect = menuTriggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right
      });
    }
  }, [isMenuOpen]);

  // Fermer le menu quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuTriggerRef.current &&
        !menuTriggerRef.current.contains(event.target) &&
        menuDropdownRef.current &&
        !menuDropdownRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const cancelExclusion = () => {
    setInfoVivier({
      idVivier: EXCLUSION_VIVIER_ID,
      nameUser:
        tempWorkerInfos?.user?.firstname +
        ' ' +
        tempWorkerInfos?.user?.lastname,
      listUsers: [tempWorkerInfos?.user?._id],
    });
    toggle('vivier_delete_candidate');
  };

  // Vérifier si le profil est exclu
  const isExcluded = useMemo(() => {
    if (!Array.isArray(tempWorkerInfos?.viviers)) return false;
    return tempWorkerInfos.viviers.some(v => {
      const vivierId = v._id?.toString ? v._id.toString() : v._id;
      return vivierId === EXCLUSION_VIVIER_ID;
    });
  }, [tempWorkerInfos?.viviers]);

  // Réinitialiser showCV quand objID change
  useEffect(() => {
    if (!objID) {
      setShowCV(false);
      setCvDocs([]);
      setCvLoadError(null);
    }
  }, [objID]);

  // Synchroniser showCV avec le parent pour ajuster la largeur du SlideOver
  useEffect(() => {
    if (setParentShowCV) {
      setParentShowCV(showCV);
    }
  }, [showCV, setParentShowCV]);

  // Mémoriser le DocViewer pour éviter les re-renders
  const cvViewer = useMemo(() => {
    if (cvDocs.length === 0) return null;
    return (
      <DocViewer
        documents={cvDocs}
        style={{
          maxHeight: '100%',
          pdfVerticalScrollByDefault: true,
        }}
        pluginRenderers={DocViewerRenderers}
      />
    );
  }, [cvDocs]);

  return (
    <>
      <div className="flex h-full gap-0">
        {showCV && (
          <div className="hidden lg:flex lg:w-[600px] bg-white border-r border-gray-200 flex-col">
            <div className="flex-1 overflow-auto p-4">
              {cvViewer ? (
                cvViewer
              ) : isWaitingCV ? (
                <div className="flex justify-center items-center h-full">
                  <div className="text-gray-500">Chargement du CV...</div>
                </div>
              ) : (
                <div className="flex justify-center items-center h-full px-4 text-center">
                  <div className="text-gray-500 text-sm">
                    {cvLoadError ||
                      'Aucun aperçu CV pour le moment.'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="w-full lg:w-[800px] flex flex-col relative">
          {isLoadingData && (
            <div className="absolute inset-0 bg-white bg-opacity-60 z-10 flex items-center justify-center pointer-events-none">
              <div className="animate-spin">
                <Loading2 wh={40} color={'#FF9600'} />
              </div>
            </div>
          )}
          <SlideOverHeader 
            hide={closeSlideOver} 
            hideShow={true}
            navigation={{
              currentIndex: slideOverCurrentIndex,
              total: currentList.length,
              onPrevious: goToPrevious,
              onNext: goToNext,
            }}
            extraActions={
              !isExcluded && (
                <div className="relative" ref={menuTriggerRef}>
                  <button
                    type={'button'}
                    className="flex items-center justify-center border rounded p-1 hover:bg-gray-100"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="10" cy="5" r="1.5" fill="#374151" />
                      <circle cx="10" cy="10" r="1.5" fill="#374151" />
                      <circle cx="10" cy="15" r="1.5" fill="#374151" />
                    </svg>
                  </button>
                  
                  {/* Dropdown menu */}
                  {isMenuOpen && ReactDOM.createPortal(
                    <div
                      ref={menuDropdownRef}
                      className="fixed z-[99999]"
                      style={{ top: menuPosition.top, right: menuPosition.right }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenu
                        items={[
                          {
                            icon: BanIcon,
                            label: 'Exclure le candidat',
                            onClick: excludeFromAgency,
                            iconColor: '#DC2626'
                          }
                        ]}
                        width="w-56"
                      />
                    </div>,
                    document.body
                  )}
                </div>
              )
            }
          >
            <div className="text-lg text-gray-900 flex gap-1 items-center">
              <div className="truncate">
                {tempWorkerInfos?.user?.firstname} {tempWorkerInfos?.user?.lastname}
              </div>
              <div className="text-gray-500 text-xs flex-shrink-0">
                <CopyToClipboard text={tempWorkerInfos?.user?.id_bestt}>
                  (N° {tempWorkerInfos?.user?.id_bestt})
                </CopyToClipboard>
              </div>
            </div>
            <div className={'flex gap-2 text-gray-500 text-sm items-center'}>
              <div className={'flex gap-1 items-center'}>
                <div className={''}>
                  {tempWorkerInfos?.user.token_refresh_mobile ? (
                    <Badge type={'APP'}>App</Badge>
                  ) : (
                    ''
                  )}
                </div>
                <div className={''}>
                  {tempWorkerInfos?.user.token_refresh ? (
                    <Badge type={'WEB'}>Web</Badge>
                  ) : (
                    ''
                  )}
                </div>
              </div>
              <div className={''}>·</div>
              <div className={'font-normal'}>
                Inscrit depuis le{' '}
                {dateWordShort(new Date(tempWorkerInfos?.user.createdAt))}
              </div>
            </div>
          </SlideOverHeader>
          <SlideOverBody>
        <div className={`flex flex-col gap-6 transition-opacity duration-200 ${isLoadingData ? 'opacity-50' : 'opacity-100'}`}>
          {/* Bandeau Profil Exclu (prioritaire) - vérifie si le profil est dans le vivier d'exclusion */}
          {isExcluded && (
            <div className={'flex gap-2 items-center border border-red-200 rounded bg-red-50 px-3 py-2 mb-6'}>
              <div className={''}>
                <BanIcon wh={20} color={'#DC2626'} />
              </div>
              <div className={'flex-1'}>
                <div className={'flex flex-col gap-2'}>
                  <div className={'text-red-700'}>Profil exclu</div>
                  <div className={'flex flex-col text-gray-500 text-sm'}>
                    <div className={''}>
                      Ce profil a été exclu de l'agence.
                    </div>
                    {userArchiveReason && !ARCHIVE_REASONS.includes(userArchiveReason) && (
                      <div className={'flex gap-2'}>
                        <div className={''}>Raison :</div>
                        <div className={'font-black'}>
                          {userArchiveReason === 'exclude'
                            ? `Exclus de l'agence`
                            : userArchiveReason}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className={'flex-shrink-0'}>
                <Button
                  color={'SECONDARY'}
                  size={'SM'}
                  onClick={cancelExclusion}
                >
                  Annuler l'exclusion
                </Button>
              </div>
            </div>
          )}
          {/* Bandeau Profil Archivé - seulement si pas dans le vivier d'exclusion */}
          {userArchiveReason && ARCHIVE_REASONS.includes(userArchiveReason) && 
           !(Array.isArray(tempWorkerInfos?.viviers) && tempWorkerInfos.viviers.some(v => v._id === EXCLUSION_VIVIER_ID)) && (
            <div className={'flex gap-2 border rounded bg-gray-50 px-3 py-2 mb-6'}>
              <div className={''}>
                <ArchiveIcon wh={20} color={'#374151'} />
              </div>
              <div className={''}>
                <div className={'flex flex-col gap-2'}>
                  <div className={''}>Profil archivé</div>
                  <div className={'flex flex-col text-gray-500 text-sm'}>
                    <div className={''}>
                      Le profil est archivé, son statut sera actualisée à sa
                      prochaine mise à jour de disponibilités.
                    </div>
                    <div className={'flex gap-2'}>
                      <div className={''}>Raison :</div>
                      <div className={'font-black'}>
                        {userArchiveReason === 'admin' &&
                          `L'administrateur a archivé le profil`}
                        {userArchiveReason === '45 days absence' &&
                          `Inactivité durant 45 jours`}
                        {userArchiveReason === 'unavailable' &&
                          `Le profil est indisponible`}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className={'flex justify-between gap-2 py-2 -mt-6'}>
            <div className={'my-auto flex gap-2 text-sm'}>
              <div
                onClick={() => setActiveTab(1)}
                className={`flex items-center justify-center  py-3 px-4 cursor-pointer text-gray-500 h-9 ${
                  activeTab === 1
                    ? ' bg-gray-100 text-gray-700 rounded-md '
                    : 'hover:bg-gray-100 hover:rounded-md hover:text-gray-700'
                }`}
              >
                Aperçu
              </div>
              <div
                onClick={() => setActiveTab(2)}
                className={`flex items-center justify-center  py-3 px-4 cursor-pointer text-gray-500 h-9 ${
                  activeTab === 2
                    ? ' bg-gray-100 text-gray-700 rounded-md '
                    : 'hover:bg-gray-100 hover:rounded-md hover:text-gray-700'
                }`}
              >
                <div className={'flex gap-1'}>
                  <div className={'my-auto'}>Avis</div>
                  <div className="my-auto bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px]">
                    {nbReviews}
                  </div>
                </div>
              </div>
              <div
                onClick={() => setActiveTab(3)}
                className={`flex items-center justify-center  py-3 px-4 cursor-pointer text-gray-500 h-9 ${
                  activeTab === 3
                    ? ' bg-gray-100 text-gray-700 rounded-md '
                    : 'hover:bg-gray-100 hover:rounded-md hover:text-gray-700'
                }`}
              >
                <div className={'flex gap-1'}>
                  <div className={'my-auto'}>Commentaires</div>
                  <div className="my-auto bg-yellow-100 text-yellow-800 rounded px-1 flex items-center justify-center min-w-[20px]">
                    {nbComments}
                  </div>
                </div>
              </div>
              <div
                onClick={() => setActiveTab(4)}
                className={`flex items-center justify-center  py-3 px-4 cursor-pointer text-gray-500 h-9 ${
                  activeTab === 4
                    ? ' bg-gray-100 text-gray-700 rounded-md '
                    : 'hover:bg-gray-100 hover:rounded-md hover:text-gray-700'
                }`}
              >
                <div className={'flex gap-1'}>
                  <div className={'my-auto'}>Historique</div>
                </div>
              </div>
              {userData?.accessPayslip && (
                <div
                  onClick={() => setActiveTab(5)}
                  className={`flex items-center justify-center  py-3 px-4 cursor-pointer text-gray-500 h-9 ${
                    activeTab === 5
                      ? ' bg-gray-100 text-gray-700 rounded-md '
                      : 'hover:bg-gray-100 hover:rounded-md hover:text-gray-700'
                  }`}
                >
                  <div className={'flex gap-1'}>
                    <div className={'my-auto'}>Paie</div>
                  </div>
                </div>
              )}
            </div>
            <div className={'my-auto'}>
              {tempWorkerInfos?.tempWorker?.cv?.updated ? (
                <div onClick={() => openDocument('cv')}>
                  <Button color={'SECONDARY'}>CV</Button>
                </div>
              ) : (
                <Button color={'DISABLED'}>CV</Button>
              )}
            </div>
          </div>
          {activeTab === 1 && (
            <PreviewTempWorker
              ref={previewRef}
              tempWorkerInfos={tempWorkerInfos}
            />
          )}
          {activeTab === 2 && (
            <ReviewTempWorker idUser={tempWorkerInfos?.user?._id} />
          )}
          {activeTab === 3 && (
            <div className={'flex-1 min-h-0'}>
              <CommentTempWorker idUser={tempWorkerInfos?.user?._id} />
            </div>
          )}
          {activeTab === 4 && (
            <HistoryTempWorker idUser={tempWorkerInfos?.user?._id} />
          )}
          {activeTab === 5 && userData?.accessPayslip && (
            <PayslipTempWorker
              idUser={tempWorkerInfos?.user?._id}
              idBestt={tempWorkerInfos?.user?.id_bestt}
              payslipData={tempWorkerInfos?.tempWorker?.payslip || []}
              initialYear={payslipInitialYear}
            />
          )}
          </div>
        </SlideOverBody>
          {slideOverMissionActions && (slideOverMissionActions.onRefuse || slideOverMissionActions.onPendingContact || slideOverMissionActions.onAssign) && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 bg-gray-800 text-white p-1.5 rounded-lg shadow-lg">
              <div className="flex items-center text-sm h-10 gap-0.5">
                {slideOverMissionActions.onRefuse && (
                  <Tooltip text="Raccourci : x" placement="top">
                    <button
                      type="button"
                      className={`flex items-center gap-1.5 py-2 px-3 rounded transition-colors ${slideOverMissionActions.isRefused ? 'bg-red-900/40' : 'hover:bg-gray-700'}`}
                      onClick={() => handleMissionAction(slideOverMissionActions.onRefuse, slideOverMissionActions.isRefused)}
                    >
                      <CrossIcon wh={18} color={slideOverMissionActions.isRefused ? '#FCA5A5' : '#F87171'} />
                      <span className={slideOverMissionActions.isRefused ? 'text-red-300' : 'text-gray-200'}>Refuser</span>
                    </button>
                  </Tooltip>
                )}
                {slideOverMissionActions.onPendingContact && (
                  <>
                    <div className="w-px h-5 bg-gray-600" />
                    <Tooltip text="Raccourci : g" placement="top">
                      <button
                        type="button"
                        className={`flex items-center gap-1.5 py-2 px-3 rounded transition-colors ${slideOverMissionActions.isPendingContact ? 'bg-yellow-900/40' : 'hover:bg-gray-700'}`}
                        onClick={() => handleMissionAction(slideOverMissionActions.onPendingContact, true)}
                      >
                        <QuestionMarkIcon wh={18} color={slideOverMissionActions.isPendingContact ? '#FDE68A' : '#FBBF24'} />
                        <span className={slideOverMissionActions.isPendingContact ? 'text-yellow-300' : 'text-gray-200'}>En attente</span>
                      </button>
                    </Tooltip>
                  </>
                )}
                {slideOverMissionActions.onAssign && (
                  <>
                    <div className="w-px h-5 bg-gray-600" />
                    <Tooltip text="Raccourci : entrée" placement="top">
                      <button
                        type="button"
                        className="flex items-center gap-1.5 py-2 px-3 rounded hover:bg-gray-700 transition-colors"
                        onClick={() => handleMissionAction(slideOverMissionActions.onAssign)}
                      >
                        <Check wh={18} color={'#34D399'} />
                        <span className="text-gray-200">Attribuer</span>
                      </button>
                    </Tooltip>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SlideOverTempWorker;
